import { Plus, Trash2 } from "lucide-react";
import { Select } from "antd";
import { cn } from "../../utils/tailwindUtils";
import { inputCls } from "./shared";

/**
 * The IF of an assignment rule, read as a sentence:
 *
 *   Match leads where [all | any] of these are true
 *     Lead source   is any of   Meta Ads, Facebook
 *     State         is          Tamil Nadu
 *
 * Optional OR groups ("(A and B) or (C and D)") for Release 2 needs, capped at
 * five so a rule stays explainable. Field and operator lists come from the
 * backend registry (customer/assignment/fields/), so nothing here can produce
 * a condition the server would refuse.
 *
 * value: {match, conditions} or {groups: [{conditions}]}
 */

const LIST_OPS = ["in", "not_in"];
const NO_VALUE = ["empty", "not_empty"];
const MAX_GROUPS = 5;

export default function ConditionBuilder({ value, onChange, fields, errors = {}, disabled, maxConditions = 20 }) {
    const byKey = Object.fromEntries((fields || []).map(f => [f.key, f]));
    const grouped = !!value?.groups;
    const groups = grouped ? value.groups : [{ conditions: value?.conditions || [] }];
    const total = groups.reduce((n, g) => n + (g.conditions || []).length, 0);

    const emit = (nextGroups, match = value?.match || "all") => {
        if (nextGroups.length > 1) onChange({ groups: nextGroups });
        else onChange({ match, conditions: nextGroups[0]?.conditions || [] });
    };

    const setCond = (gi, ci, patch) => {
        const next = groups.map((g, i) => i !== gi ? g : {
            conditions: g.conditions.map((c, j) => j !== ci ? c : { ...c, ...patch }),
        });
        emit(next);
    };
    const addCond = (gi) => {
        const first = fields?.[0];
        const next = groups.map((g, i) => i !== gi ? g : {
            conditions: [...g.conditions, { field: first?.key || "lead_source", op: first?.ops?.[0]?.value || "eq", value: "" }],
        });
        emit(next);
    };
    const removeCond = (gi, ci) => {
        let next = groups.map((g, i) => i !== gi ? g : { conditions: g.conditions.filter((_, j) => j !== ci) });
        if (next.length > 1) next = next.filter(g => g.conditions.length);
        emit(next.length ? next : [{ conditions: [] }]);
    };
    const addGroup = () => emit([...groups, { conditions: [{ field: fields?.[0]?.key, op: fields?.[0]?.ops?.[0]?.value, value: "" }] }]);

    const errPath = (gi, ci) => grouped
        ? `conditions.groups[${gi}].conditions[${ci}]` : `conditions.conditions[${ci}]`;

    return (
        <div className="space-y-2">
            {!grouped && (
                <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-200">
                    Match leads where
                    <select value={value?.match || "all"} disabled={disabled}
                        onChange={e => onChange({ ...value, match: e.target.value, conditions: value?.conditions || [] })}
                        className="text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-1.5 py-0.5">
                        <option value="all">all</option>
                        <option value="any">any</option>
                    </select>
                    of these are true
                </div>
            )}

            {groups.map((g, gi) => (
                <div key={gi} className={cn(grouped && "rounded-lg border border-gray-200 dark:border-gray-700 p-2")}>
                    {grouped && (
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                            {gi === 0 ? "All of these" : "Or all of these"}
                        </p>
                    )}
                    <div className="space-y-1.5">
                        {(g.conditions || []).map((c, ci) => {
                            const f = byKey[c.field] || fields?.[0];
                            const p = errPath(gi, ci);
                            const err = errors[`${p}.field`] || errors[`${p}.op`] || errors[`${p}.value`];
                            return (
                                <div key={ci}>
                                    <div className={cn("flex flex-wrap md:flex-nowrap items-start gap-1.5",
                                        err && "rounded ring-1 ring-red-300 p-1")}>
                                        <select value={c.field} disabled={disabled} aria-label="Field"
                                            onChange={e => {
                                                const nf = byKey[e.target.value];
                                                setCond(gi, ci, { field: e.target.value, op: nf?.ops?.[0]?.value, value: "" });
                                            }}
                                            className={cn(inputCls, "md:w-44")}>
                                            {(fields || []).map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
                                        </select>
                                        <select value={c.op} disabled={disabled} aria-label="Operator"
                                            onChange={e => {
                                                const op = e.target.value;
                                                const wasList = LIST_OPS.includes(c.op), isList = LIST_OPS.includes(op);
                                                let v = c.value;
                                                if (wasList !== isList) v = isList ? (v ? [String(v)] : []) : (Array.isArray(v) ? v[0] || "" : v);
                                                setCond(gi, ci, { op, value: NO_VALUE.includes(op) ? undefined : v });
                                            }}
                                            className={cn(inputCls, "md:w-36")}>
                                            {(f?.ops || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </select>
                                        <div className="flex-1 min-w-[10rem]">
                                            <ValueInput field={f} cond={c} disabled={disabled}
                                                onChange={v => setCond(gi, ci, { value: v })} />
                                        </div>
                                        {!disabled && (
                                            <button type="button" onClick={() => removeCond(gi, ci)} title="Remove condition"
                                                className="h-8 w-8 flex items-center justify-center rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20">
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </div>
                                    {err && <p className="text-[11px] text-red-600 mt-0.5">{err[0]}</p>}
                                    {!err && ["neq", "not_in", "not_contains"].includes(c.op) && (
                                        <p className="text-[10px] text-gray-400 mt-0.5">Leads with no {f?.label?.toLowerCase()} also match.</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {!disabled && (
                        <button type="button" onClick={() => addCond(gi)} disabled={total >= maxConditions}
                            title={total >= maxConditions ? "Split into two rules" : ""}
                            className="mt-1.5 text-[11px] text-primary hover:underline inline-flex items-center gap-0.5 disabled:opacity-40">
                            <Plus className="h-3 w-3" /> Add condition
                        </button>
                    )}
                </div>
            ))}

            {!total && <p className="text-[11px] text-gray-500">No conditions: this rule matches every lead.</p>}
            {errors.conditions && <p className="text-[11px] text-red-600">{errors.conditions[0]}</p>}

            {!disabled && total > 0 && groups.length < MAX_GROUPS && (
                <button type="button" onClick={addGroup}
                    className="text-[11px] text-gray-500 hover:text-primary inline-flex items-center gap-0.5">
                    <Plus className="h-3 w-3" /> Add an OR group
                </button>
            )}
        </div>
    );
}

function ValueInput({ field, cond, onChange, disabled }) {
    if (!field || NO_VALUE.includes(cond.op)) return <div className="h-8" />;
    const isList = LIST_OPS.includes(cond.op);
    const options = field.options
        || (field.suggestions || []).map(s => ({ value: s, label: s }));

    if (field.type === "number") {
        return <input type="number" value={cond.value ?? ""} disabled={disabled} aria-label="Value"
            onChange={e => onChange(e.target.value)} className={inputCls} />;
    }
    if (isList) {
        return (
            <Select mode={field.options ? "multiple" : "tags"} size="middle" className="w-full" disabled={disabled}
                value={Array.isArray(cond.value) ? cond.value.map(String) : []} onChange={onChange}
                options={options.map(o => ({ value: String(o.value), label: o.label }))}
                placeholder={field.options ? "Pick values" : "Type or pick values"}
                tokenSeparators={field.options ? undefined : [","]} maxTagCount="responsive"
                filterOption={(input, o) => (o?.label || "").toLowerCase().includes(input.toLowerCase())} />
        );
    }
    if (field.options) {
        return (
            <select value={cond.value ?? ""} disabled={disabled} aria-label="Value"
                onChange={e => onChange(e.target.value)} className={inputCls}>
                <option value="">Pick…</option>
                {field.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        );
    }
    const listId = `sugg-${field.key}`;
    return (
        <>
            <input value={cond.value ?? ""} disabled={disabled} list={listId} aria-label="Value"
                onChange={e => onChange(e.target.value)} className={inputCls} placeholder="Value" />
            <datalist id={listId}>{(field.suggestions || []).map(s => <option key={s} value={s} />)}</datalist>
        </>
    );
}
